import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";

import { checkoutAtom } from "@/atoms/checkoutAtom";
import { faPenToSquare, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import { Box, Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";

import longVerticalLink from "@assets/img/longVerticalLink.svg";
import shortVerticalLink from "@assets/img/shortVerticalLink.svg";

import { formatPrice, getTrackingId } from "@utils/utils";

import { Text } from "@components/Text";

import { CheckoutInfoInterface } from "../CheckoutDetailsContainer";

interface CouponInfoSectionPropTypes {
    discountType: "percentage" | "fixed";
    discountPercentage: number | null;
    discountAmount: number | null;
    reducedPrice: number;
}

interface PropTypes extends CheckoutInfoInterface {
    addonSectionTitle?: string;
    haveExternalAddons?: boolean;
    plan: string;
    onRemoveCoupon?: () => void;
}

const CouponInfoSection = ({
    discountType,
    discountAmount,
    discountPercentage,
    reducedPrice
}: CouponInfoSectionPropTypes) => {
    return (
        <Flex my={0.9} justifyContent="space-between" alignItems="center">
            {/* Coupon Info Section */}
            <Box bg="gray.200" px={2} borderRadius={5}>
                <Text fontSize={12} lineHeight={2}>
                    <Trans
                        defaults="Coupon discount: {{ discount }}"
                        values={{
                            discount:
                                discountType === "percentage"
                                    ? `${discountPercentage}%`
                                    : `$${discountAmount}`
                        }}
                    />
                </Text>
            </Box>
            {/* Coupon Price Section */}
            <Flex justify="center" align="center" gap={3}>
                <Text fontFamily="inter, sans-serif" fontSize={14}>
                    -{formatPrice(reducedPrice)}
                </Text>
                <Box>
                    <FontAwesomeIcon
                        icon={faPenToSquare}
                        color="white"
                        fontSize={14}
                    />
                </Box>
            </Flex>
        </Flex>
    );
};

// eslint-disable-next-line complexity
const CheckoutSummary = ({
    product,
    discount,
    addon,
    addonSectionTitle,
    haveExternalAddons,
    plan,
    onRemoveCoupon
}: PropTypes) => {
    const navigate = useNavigate();

    const { routeWithQueryParams } = useCheckoutQueryParams();

    const [checkout] = useAtom(checkoutAtom);

    const selectedName = checkout.selectedStudent
        ? checkout.selectedStudent.first_name
        : null;

    const showLongLink =
        addon &&
        (product.isDiscountApplied ||
            (product.isDiscountApplied && addon.isDiscountApplied));
    const showShortLink =
        addon &&
        (!product.isDiscountApplied ||
            (!product.isDiscountApplied && !addon.isDiscountApplied));
    const showNoLink = !addon && product.isDiscountApplied;

    return (
        <Flex direction="column">
            <Flex
                w="full"
                py={3}
                direction="column"
                borderBottom="1px solid"
                borderColor="gray.200"
            >
                <Text color="gray.600" fontSize={12} textTransform="uppercase">
                    {selectedName ? (
                        <Trans
                            defaults="Program for {{ selectedName }}"
                            values={{ selectedName }}
                        />
                    ) : (
                        <Trans>Program</Trans>
                    )}
                </Text>
                <Flex>
                    {/* Checkout Item Section */}
                    <Flex direction="column" w="full">
                        {/* Checkout Item Name Section */}
                        <Flex justifyContent="space-between">
                            {/* Plan Name Section */}
                            <Text
                                type="subHeading"
                                fontFamily="inter, sans-serif"
                                fontSize={14}
                                position="relative"
                                _before={
                                    showShortLink
                                        ? {
                                              content: `url('${shortVerticalLink}')`,
                                              position: "absolute",
                                              bottom: "-27px",
                                              left: "2px"
                                          }
                                        : showLongLink
                                        ? {
                                              content: `url('${longVerticalLink}')`,
                                              position: "absolute",
                                              bottom: "-59px",
                                              left: "2px"
                                          }
                                        : {}
                                }
                            >
                                {product.name} - {plan}: {product.grade}
                            </Text>
                            {/* Checkout Item Price Section */}
                            <Flex justify="center" align="center" gap={3}>
                                {product.isAlreadyPurchased ? (
                                    <>
                                        <Text
                                            fontFamily="inter, sans-serif"
                                            fontSize={14}
                                        >
                                            <Trans>Purchased</Trans>
                                        </Text>
                                        <Box>
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                                color="white"
                                                fontSize={14}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Text
                                            fontFamily="inter, sans-serif"
                                            fontSize={14}
                                        >
                                            {formatPrice(product.price, product.currency)}
                                        </Text>
                                        <Box
                                            opacity={
                                                product.isEditable ? "1" : "0.5"
                                            }
                                            layerStyle="checkoutInfoEditLayoutStyle"
                                            className={ClassesEnum.EditButton}
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.PaymentDetailsContainer,
                                                    ClassesEnum.EditButton
                                                ],
                                                "changePlan"
                                            )}
                                            cursor={
                                                product.isEditable
                                                    ? "pointer"
                                                    : "not-allowed"
                                            }
                                            onClick={
                                                product.isEditable
                                                    ? () =>
                                                          navigate(
                                                              routeWithQueryParams(
                                                                  CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                                                              )
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <FontAwesomeIcon
                                                icon={faPenToSquare}
                                                color="var(--euka-colors-burntYellow-500)"
                                                fontSize={14}
                                            />
                                        </Box>
                                    </>
                                )}
                            </Flex>
                        </Flex>
                        {/* Coupon Info Section */}
                        {product.isDiscountApplied && (
                            <Box ml={!showNoLink ? 4 : 0}>
                                <CouponInfoSection
                                    discountAmount={discount!.discountAmount}
                                    discountPercentage={
                                        discount!.discountPercentage
                                    }
                                    discountType={discount!.discountType}
                                    reducedPrice={product.reducedPrice}
                                />
                            </Box>
                        )}
                    </Flex>
                </Flex>
                {/* Premium Services List Section */}
                {addon && (
                    <Flex direction="column" pl={4}>
                        <Text color="gray.600" fontSize={12}>
                            {addonSectionTitle ?? (
                                <Trans>Premium Services</Trans>
                            )}
                        </Text>
                        {/* Checkout Item Section */}
                        <Flex direction="column" w="full" mt={-1}>
                            <Flex justify="space-between">
                                {/* Checkout Item Name Section */}
                                <Text
                                    fontFamily="inter, sans-serif"
                                    fontSize={14}
                                    maxW="60%"
                                    fontWeight={
                                        haveExternalAddons ? "bold" : "normal"
                                    }
                                >
                                    {addon.name}
                                </Text>
                                {/* Checkout Item Price Section */}
                                <Flex justify="center" align="center" gap={3}>
                                    <Text
                                        fontFamily="inter, sans-serif"
                                        fontSize={14}
                                    >
                                        {formatPrice(addon.price, product.currency)}
                                    </Text>
                                    <Box
                                        opacity={addon.isEditable ? "1" : "0.5"}
                                        layerStyle="checkoutInfoEditLayoutStyle"
                                        className={ClassesEnum.EditButton}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.PaymentDetailsContainer,
                                                ClassesEnum.EditButton
                                            ],
                                            "changeAddon"
                                        )}
                                        cursor={
                                            addon.isEditable
                                                ? "pointer"
                                                : "not-allowed"
                                        }
                                        onClick={
                                            addon.isEditable
                                                ? () =>
                                                      navigate(
                                                          routeWithQueryParams(
                                                              CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                                                          )
                                                      )
                                                : undefined
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            color="var(--euka-colors-burntYellow-500)"
                                            fontSize={14}
                                        />
                                    </Box>
                                </Flex>
                            </Flex>
                            {/* Coupon Info Section */}
                            {addon.isDiscountApplied && (
                                <CouponInfoSection
                                    discountAmount={discount!.discountAmount}
                                    discountPercentage={
                                        discount!.discountPercentage
                                    }
                                    discountType={discount!.discountType}
                                    reducedPrice={addon.reducedPrice}
                                />
                            )}
                        </Flex>
                    </Flex>
                )}
            </Flex>
            {discount && (
                <Flex
                    w="full"
                    py={3}
                    direction="column"
                    borderBottom="1px solid"
                    borderColor="gray.200"
                >
                    <Flex justify="space-between" alignItems="start">
                        {/* Total Discount Name Section */}
                        <Flex direction="column" gap={1} maxW="60%">
                            <Text
                                type="subHeading"
                                fontFamily="inter, sans-serif"
                                fontSize={14}
                            >
                                {discount.appliedOn ===
                                "each_specified_item" ? (
                                    <Trans>Total discount applied</Trans>
                                ) : (
                                    <Trans>Discount applied</Trans>
                                )}
                            </Text>
                            <Flex direction="column" gap={3}>
                                <Text
                                    color="gray.600"
                                    fontSize={12}
                                    lineHeight="15px"
                                >
                                    Coupon: {discount.code.toUpperCase()} (
                                    {discount.invoiceName
                                        ? discount.invoiceName + ", "
                                        : ""}
                                    Discount:{" "}
                                    {discount.discountType === "percentage"
                                        ? `${discount.discountPercentage}%`
                                        : `$${discount.discountAmount}`}
                                    )
                                </Text>
                                {discount.durationType === "forever" && (
                                    <Text
                                        color="gray.600"
                                        fontSize={12}
                                        lineHeight="15px"
                                    >
                                        <Trans>
                                            NOTE: This is a recurring coupon and
                                            will be automatically applied to
                                            each future renewal.
                                        </Trans>
                                    </Text>
                                )}
                                {discount.durationType === "limited_period" && (
                                    <Text
                                        color="gray.600"
                                        fontSize={12}
                                        lineHeight="15px"
                                    >
                                        <Trans
                                            defaults="NOTE: This coupon is valid for {{ couponValid }} and will be automatically applied to future renewals if still valid."
                                            values={{
                                                couponValid:
                                                    discount?.durationLimit
                                            }}
                                        />
                                    </Text>
                                )}
                            </Flex>
                        </Flex>
                        {/* Total Discount Price Section */}
                        <Flex justify="center" align="center" gap={3}>
                            <Text fontFamily="inter, sans-serif" fontSize={14}>
                                -{formatPrice(discount.totalReducedPrice, product.currency ?? addon?.currency ?? "AUD")}
                            </Text>
                            <Box
                                opacity={discount.isEditable ? "1" : "0.5"}
                                layerStyle="checkoutInfoEditLayoutStyle"
                                className={ClassesEnum.EditButton}
                                data-tracking-id={getTrackingId(
                                    [
                                        ClassesEnum.PaymentDetailsContainer,
                                        ClassesEnum.EditButton
                                    ],
                                    "changeCouponCode"
                                )}
                                cursor={
                                    discount.isEditable
                                        ? "pointer"
                                        : "not-allowed"
                                }
                                onClick={
                                    discount.isEditable
                                        ? onRemoveCoupon
                                        : undefined
                                }
                            >
                                <FontAwesomeIcon
                                    data-testid="deleteItem"
                                    icon={faXmark}
                                    color="var(--euka-colors-burntYellow-500)"
                                    fontSize={14}
                                />
                            </Box>
                        </Flex>
                    </Flex>
                </Flex>
            )}
        </Flex>
    );
};

export default CheckoutSummary;
